import { sendLogToServer } from '../../utils/log';
/**
 * Вычисляет расстояние Левенштейна между двумя строками.
 * @param {string} a - Первая строка.
 * @param {string} b - Вторая строка.
 * @returns {number} - Расстояние Левенштейна.
 */
function levenshteinDistance(a, b) {
  const dp = Array(b.length + 1).fill().map(() => Array(a.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[0][i] = i;
  for (let j = 0; j <= b.length; j++) dp[j][0] = j;

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[j][i] = Math.min(
        dp[j - 1][i] + 1, // удаление
        dp[j][i - 1] + 1, // вставка
        dp[j - 1][i - 1] + cost // замена  
      );
    }
  }
  return dp[b.length][a.length];
}

/**
 * Список игнорируемых слов (артиклей, предлогов, местоимений) для каждого языка.
 */
const ignorableWordsByLanguage = {
  de: new Set([
    'der', 'die', 'das', 'dem', 'den', 'des', 'ein', 'eine', 'einen', 'einem',
    'es', 'mit', 'in', 'an', 'auf', 'für', 'bei', 'zu', 'aus',
  ]),

  en: new Set([
    'the', 'a', 'an',
    'is', 'are', 'was', 'were', 'it', 'this', 'that',
    'in', 'on', 'at', 'to', 'for', 'with', 'by', 'from',
  ]),

  ru: new Set([
    'и', 'а', 'но', 'или', 'в', 'на', 'с', 'по', 'к', 'у', 'из', 'о',
    'это', 'тот', 'та', 'те', 'этот', 'эта', 'эти',
  ]),

  uk: new Set([
    // союзы
    'і', 'й', 'та', 'але', 'або', 'чи',

    // предлоги
    'в', 'у', 'на', 'з', 'із', 'зі', 'по', 'до', 'від', 'для', 'про', 'при',

    // местоимения и указательные
    'це', 'той', 'та', 'ті', 'цей', 'ця', 'ці',
  ]),
};


/**
 * Сравнивает распознанный текст с массивом опций на основе слов.
 * Учитывает расстояние Левенштейна и игнорирует артикли/служебные слова в зависимости от языка.
 * @param {string} recognizedText - Распознанный текст.
 * @param {Array<{userText: string}>} options - Массив опций с полем userText.
 * @param {string} language - Код языка (например, 'de', 'en', 'ru').
 * @returns {number|null} - Индекс совпавшей опции (0 или i+1) или null.
 */
export function compareText(recognizedText, options, language = "de") {
  
  if (!recognizedText || !options || !Array.isArray(options)) {
    //console.warn('⚠️ Некорректные входные данные: recognizedText или options отсутствуют');
    return null;
  }

  const normalizedRecognized = recognizedText.toLowerCase().trim();
  // Получаем список игнорируемых слов для указанного языка или пустой набор, если язык неизвестен
  const ignorableWords = ignorableWordsByLanguage[language?.toLowerCase()] || new Set();
  const recognizedWords = normalizedRecognized.split(/\s+/).filter(word => word);

  let bestMatch = {
    index: null,
    matchedWords: 0,
    totalWords: 0,
    similarityScore: 0,
    unmatchedWords: [],
    extraWords: [],
  };

  for (let i = 0; i < options.length; i++) {
    const optionText = options[i].userText?.toLowerCase().trim();
    if (!optionText) {
      //console.warn(`⚠️ Опция ${i} не содержит userText`);
      continue;
    }

    const expectedWords = optionText.split(/\s+/).filter(word => word);
    let matchedWords = 0;
    const unmatchedWords = [];
    const usedRecognizedIndices = new Set();

    expectedWords.forEach((word, wordIndex) => {
      // Игнорируем артикли и служебные слова для данного языка
      if (ignorableWords.has(word)) {
        matchedWords++; // Считаем служебное слово совпавшим
        return;
      }

      // Ищем лучшее совпадение с учетом расстояния Левенштейна
      let bestMatchIndex = -1;
      let minDistance = Infinity;
      recognizedWords.forEach((rw, idx) => {
        if (!usedRecognizedIndices.has(idx)) {
          const distance = levenshteinDistance(word, rw);
          // Считаем совпадением, если расстояние <= 2 или слова идентичны
          if ((distance <= 2 || word === rw) && distance < minDistance) {
            minDistance = distance;
            bestMatchIndex = idx;
          }
        }
      });

      if (bestMatchIndex !== -1) {
        matchedWords++;
        usedRecognizedIndices.add(bestMatchIndex);
      } else {
        unmatchedWords.push(word);
      }
    });

    const extraWords = recognizedWords.filter(
      (_, idx) => !usedRecognizedIndices.has(idx)
    );
    const totalWords = expectedWords.length;
    const similarityScore = totalWords > 0 ? (matchedWords / totalWords) * 100 : 0;

    if (
      similarityScore === 100 ||
      (similarityScore >= 75 &&
        (bestMatch.index === null ||
          similarityScore > bestMatch.similarityScore ||
          (similarityScore === bestMatch.similarityScore &&
            extraWords.length < bestMatch.extraWords.length)))
    ) {
      bestMatch = {
        index: i,
        matchedWords,
        totalWords,
        similarityScore,
        unmatchedWords,
        extraWords,
      };
    }
  }

  if (bestMatch.index !== null) {
    const returnIndex = options.length === 1 ? 0 : bestMatch.index + 1;
	//sendLogToServer(`🎉 Выбрана опция ${returnIndex}: ${bestMatch.similarityScore.toFixed(2)}% совпадений. Распознанный текст ${recognizedText}`);
    //console.log(`🎉 Выбрана опция ${returnIndex}: ${bestMatch.similarityScore.toFixed(2)}% совпадений. Распознанный текст ${recognizedText}`);
    return returnIndex;
  }

  //console.log('😔 Ни одна опция не подошла');
  //sendLogToServer(`😔 Ни одна опция не подошла`);
  return null;
}
